import { CheckCircleFilled, DoubleRightOutlined } from "@ant-design/icons";
import { Button, Checkbox, Divider, Spin, Tooltip } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import CommonIcons from "../../../assets/icons";
import useToggle from "../../../hooks/useToggle";
import
{
   setCurrentProvider,
   setDataImages,
   setLoadingProvider,
} from "../../../store/reducers/searchSlice";
import { RootState } from "../../../store/store";
import CommonComponents from "../../Commons";
import NoData from "../../NoData";
import CardSearchResult from "./CardSearchResult";
import ImageInfo from "./ImageInfo";
import
{
   addImagesToCart,
   flyToGeometries,
   flyToGeometry,
   ImageItem,
   mouseLeaveItem,
   mouseMoveItem,
   removeFootprint,
   removeImageFromMap,
   removeImagesFromCart,
   showFootprint,
   showImageOnMap,
} from "./ultis";

import { useMapContext } from "../MapAdapter/core/MapContext";
import LazyLoad from 'react-lazyload'
import LoadingGlobalComp from "../../Commons/Loading";
import LogoSpinner from "../../Commons/Loading/LogoSpinner";
import InfiniteScroll from "react-infinite-scroll-component";

interface ResultSearchProps
{
   data: any;
   setShowResultSearch: (val: boolean) => void;
   providers: any;
   providerCode: any;
   setProviderCode: (providerCode: any) => void;
   setData: (data: any) => void;
   mapInstance: any;
   handleScroll: (e?: any) => void | any;
   providerId: any;
   loading: boolean;
   setLoading: (val: boolean) => void;
}

export default function ResultSearch({
   data,
   setShowResultSearch,
   providers,
   providerCode,
   setProviderCode,
   setData,
   mapInstance,
   handleScroll,
   providerId,
   loading,
   setLoading,
}: ResultSearchProps)
{
   const dispatch = useDispatch();
   const { t } = useTranslation();
   const { engine } = useMapContext();

   const [selectedImages, setSelectedImages] = useState([]);
   const [selectedView, setSelectedView] = useState<any>(undefined);

   let searchState = useSelector((state: RootState) => state.search);

   let arrProviders = useSelector(
      (state: RootState) => state.appCommonSlice.allProviders,
   );
   const idsImageInCart = useSelector(
      (state: RootState) => state.cartSlice.arrIdImage,
   );

   const layersOnMap = useSelector((state: RootState) => state.layer.layers);

   const layerIds = useMemo(
      () => new Set(layersOnMap.map((l: any) => l.id)),
      [layersOnMap],
   );
   let providerName = arrProviders
      ?.filter((itemF: any) => itemF?.code === providerCode)?.[0]
      ?.name.toLowerCase();

   const isAllFootprintShown =
      selectedImages.length > 0 &&
      selectedImages.every((img: any) => layerIds.has(`footerprint-${img?.id}`));
   const isAllImageShown =
      selectedImages.length > 0 &&
      selectedImages.every((img: any) => layerIds.has(`image-${img?.id}`));
   const isAllInCart =
      selectedImages.length > 0 &&
      selectedImages.every((img: any) => idsImageInCart.includes(img?.id));

   const {
      open: openViewInfo,
      shouldRender: shouldRenderViewInfo,
      toggle: toggleViewInfo,
   } = useToggle();


   const handleChangeProvider = (item: any) =>
   {
      //  @ts-ignore
      if (!searchState[`loading_provider_${item.code}`])
      {
         setSelectedImages([])
         dispatch(setLoadingProvider({ code: item.code, value: true }));
         dispatch(setCurrentProvider(item.code));

         setProviderCode(item?.code);
         //  @ts-ignore
         const data = searchState[`data_images_${item.code}`] ?? [];

         dispatch(setDataImages({
            code: item.code,
            data
         }));
         setData(data);
         dispatch(setLoadingProvider({ code: item.code, value: false }));
      }
   };

   const renderProvider = () =>
   {
      let a = providers?.map((item: any) =>
      {
         //  @ts-ignore
         const isLoading = !!searchState[`loading_provider_${item.code}`];
         const isActive = providerCode === item?.code;

         // Fixed 68x68 box with a constant 2px border (transparent when inactive)
         // + 2px padding so the 60px logo always fits exactly. The loading spinner
         // is an absolute overlay, so toggling loading never changes the layout.
         return (
            <Tooltip key={item?.code} title={item?.name}>
               <div
                  className="relative shrink-0 box-border h-[68px] w-[68px] p-[2px] rounded-lg border-2 border-solid"
                  style={{
                     borderColor: isActive
                        ? "var(--color-bg-btn-nav-primary)"
                        : "transparent",
                     cursor: isLoading ? "no-drop" : "pointer",
                  }}
                  onClick={() =>
                  {
                     handleChangeProvider(item);
                  }}
               >
                  <div
                     className={"div-provider-item relative overflow-hidden !bg-white"}
                     style={{
                        background: `url(${item?.logo_url}) center center / contain no-repeat`,
                        cursor: "inherit",
                     }}
                  >
                     {(item?.total > 0 ||
                        // @ts-ignore
                        searchState[`total_images_${item.code}`] > 0) && (
                           <div
                              className="div-total absolute bottom-0 div-one-center"
                              style={{ cursor: "inherit" }}
                           >
                              {/* @ts-ignore */}
                              {searchState[`total_images_${item.code}`] ?? item?.total}
                           </div>
                        )}
                  </div>
                  {isLoading && (
                     <div className="absolute inset-[2px] flex items-center justify-center rounded bg-black/40">
                        <LogoSpinner size={32} />
                     </div>
                  )}
               </div>
            </Tooltip>
         );
      });

      return a;
   };

   const [loadingViewInfo, setLoadingViewInfo] = useState(false);

   const handleViewInfo = (item?: any) =>
   {
      setLoadingViewInfo(true);
      setSelectedView(item);
      toggleViewInfo();
   };

   useEffect(() =>
   {
      if (openViewInfo) setLoadingViewInfo(false);
   }, [openViewInfo]);

   const pageSize = 20;
   // How many already-loaded items are revealed. Monotonic; capped by data.length
   // when sliced. Grows by pageSize per scroll; once it passes the loaded length
   // we fetch the next BE page.
   const [count, setCount] = useState(pageSize);
   const scrollRef = useRef<HTMLDivElement>(null);

   const visibleData = useMemo(() => data.slice(0, count), [data, count]);

   // Reset the reveal window on a new search (data cleared to []) or provider switch.
   useEffect(() =>
   {
      if (data.length === 0)
      {
         setCount(pageSize);
         if (scrollRef.current) scrollRef.current.scrollTop = 0;
      }
   }, [data.length]);

   useEffect(() =>
   {
      setCount(pageSize);
      if (scrollRef.current) scrollRef.current.scrollTop = 0;
   }, [providerCode]);

   const fetchMore = () =>
   {
      // Reveal the next chunk of already-loaded items.
      setCount((c) => c + pageSize);

      // If that consumes everything loaded and BE has a next page, fetch it.
      if (count + pageSize >= data.length)
      {
         // @ts-ignore
         const loadingProvider = searchState[`loading_provider_${providerCode}`];
         // @ts-ignore
         const nextLink = searchState[`next_link_${providerCode}`];
         if (!loadingProvider && nextLink?.href)
         {
            handleScroll();
         }
      }
   };

   // @ts-ignore
   const isLoadingProvider = !!searchState[`loading_provider_${providerCode}`];
   const hasMore =
      visibleData.length < data.length ||
      // @ts-ignore
      !!searchState[`next_link_${providerCode}`]?.href;

   // "View more": fetch the provider's next page, show everything loaded, then
   // scroll to the first card of the new page.
   const [scrollToIndex, setScrollToIndex] = useState<number | null>(null);

   const viewMore = () =>
   {
      // @ts-ignore
      const nextLink = searchState[`next_link_${providerCode}`];
      if (nextLink?.href)
      {
         if (isLoadingProvider) return;
         setScrollToIndex(data.length);
         setCount(Number.MAX_SAFE_INTEGER);
         handleScroll();
         return;
      }
      // Provider fully fetched, some loaded cards still hidden: show them all.
      setScrollToIndex(visibleData.length);
      setCount(Number.MAX_SAFE_INTEGER);
   };

   // Runs again when the next BE page lands, if the first new card is not there yet.
   useEffect(() =>
   {
      if (scrollToIndex === null) return;
      const container = scrollRef.current;
      const card = container?.querySelector<HTMLElement>(`[data-result-index="${scrollToIndex}"]`);
      if (!container || !card) return;
      container.scrollTo({
         top: card.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop - 8,
         behavior: "smooth",
      });
      setScrollToIndex(null);
   }, [scrollToIndex, visibleData.length]);

   // A new search / provider drops a pending scroll.
   useEffect(() => setScrollToIndex(null), [providerCode]);

   return (
      <div className="!bg-[var(--neutral-1000)] text-white rounded-xl w-full h-full flex flex-col overflow-hidden">
         {/* HEADER - Sticky */}
         <div className="sat-search-header flex justify-between items-center gap-3 p-4 sticky top-0 bg-[var(--neutral-1000)] z-10">
            <h2 className="text-lg font-semibold">{t("Search result")}</h2>

            <Tooltip title={t("Back to filter")}>
            <div
               role="button"
               aria-label={t("Back to filter")}
               className="back-to-result !bg-[var(--neutral-950)] cursor-pointer  rounded-lg border-none text-white px-3  py-2 h-8 flex gap-2 items-center whitespace-nowrap shrink-0"
               onClick={() =>
               {
                  // @ts-ignore
                  if (!searchState[`loading_provider_${providerCode}`])
                  {
                     setShowResultSearch(false);
                  }
               }}
               style={{
                  // @ts-ignore
                  cursor: searchState[`loading_provider_${providerCode}`]
                     ? "not-allowed"
                     : "pointer",
               }}
            >
               <CommonIcons.FilterIcon />
               <span className="back-label">{t("Back to filter")}</span>
            </div>
            </Tooltip>
         </div>

         {/* FILTER - Sticky */}
         <div className="result-filter-bar flex items-center justify-between gap-2 p-4 sticky h-[68px] top-[64px] bg-[var(--neutral-1000)] z-10">
            <Checkbox
               // @ts-ignore
               disabled={searchState[`loading_provider_${providerCode}`]}
               onChange={(e) =>
               {
                  if (e.target.checked)
                  {
                     setSelectedImages(data);
                  } else
                  {
                     setSelectedImages([]);
                  }
               }}
               className="shrink-0 whitespace-nowrap text-white"
               checked={selectedImages.length === data.length && data.length > 0}
               indeterminate={
                  selectedImages.length > 0 && selectedImages.length < data.length
               }
            >
               {t("All")}
            </Checkbox>
            <div className="flex items-center gap-2">
            {selectedImages?.length > 0 && (
               <div className="flex items-center gap-2 text-sm text-gray-400">
                  <Tooltip title={`${selectedImages.length} ${t("Images Selected")}`}>
                     <span className="whitespace-nowrap">
                        <span className="sel-count">
                           <CheckCircleFilled className="sel-icon icon-inherit" />
                           <b>{selectedImages.length}</b>
                        </span>
                        <span className="sel-label"> {t("Images Selected")}</span>
                     </span>
                  </Tooltip>
                  <Tooltip
                     title={
                        isAllImageShown
                           ? t("Hide image on map")
                           : t("Show image on map")
                     }
                  >
                     <Button
                        // @ts-ignore
                        disabled={searchState[`loading_provider_${providerCode}`]}
                        className={`btn-action ${isAllImageShown ? "!bg-[#4CC2FF] !text-black" : ""}`}
                        icon={<CommonIcons.MapIcon />}
                        onClick={() =>
                        {
                           selectedImages.forEach((img: any) =>
                           {
                              const imageLayerId = `image-${img?.id}`;

                              if (isAllImageShown)
                              {
                                 removeImageFromMap(img, dispatch);
                              } else if (!layerIds.has(imageLayerId))
                              {
                                 showImageOnMap(img, dispatch, providerCode, mapInstance, true);
                              }
                           });

                           if (!isAllImageShown)
                           {
                              flyToGeometries(
                                 selectedImages.map((img: any) => img?.geometry),
                                 mapInstance,
                              );
                           }
                        }}
                     />
                  </Tooltip>
                  <Tooltip
                     title={
                        isAllFootprintShown
                           ? t("Hide footprint's image on map")
                           : t("Show footprint's image on map")
                     }
                  >
                     <Button
                        // @ts-ignore
                        disabled={searchState[`loading_provider_${providerCode}`]}
                        className={`btn-action ${isAllFootprintShown ? "!bg-[#4CC2FF] !text-black" : ""}`}
                        icon={<CommonIcons.FootIcon />}
                        onClick={() =>
                        {
                           selectedImages.forEach((img: any) =>
                           {
                              const footprintLayerId = `footerprint-${img?.id}`;

                              if (isAllFootprintShown)
                              {
                                 removeFootprint(img, dispatch);
                              } else if (!layerIds.has(footprintLayerId))
                              {
                                 showFootprint(img, dispatch, mapInstance, true);
                              }
                           });

                           if (!isAllFootprintShown)
                           {
                              flyToGeometries(
                                 selectedImages.map((img: any) => img?.geometry),
                                 mapInstance,
                              );
                           }
                        }}
                     />
                  </Tooltip>

                  <Tooltip
                     title={isAllInCart ? t("Remove from cart") : t("Add to cart")}
                  >
                     <Button
                        // @ts-ignore
                        disabled={searchState[`loading_provider_${providerCode}`]}
                        className={`btn-action ${isAllInCart ? "!bg-[#4CC2FF] !text-black" : ""}`}
                        icon={<CommonIcons.CartOutlinedIcon />}
                        onClick={() =>
                        {
                           const ids = selectedImages.map((img: any) => img?.id);

                           if (isAllInCart)
                           {
                              removeImagesFromCart(
                                 ids,
                                 dispatch,
                                 providerCode,
                                 idsImageInCart,
                                 t,
                              );
                           } else
                           {
                              const idsToAdd = ids.filter(
                                 (id: any) => !idsImageInCart.includes(id),
                              );

                              if (idsToAdd.length)
                              {
                                 addImagesToCart(
                                    idsToAdd,
                                    dispatch,
                                    providerCode,
                                    idsImageInCart,
                                    t,
                                 );
                              }
                           }
                        }}
                     />
                  </Tooltip>
               </div>
            )}

            {/* Load the next page without scrolling to the bottom. */}
            {hasMore && visibleData.length > 0 && (
               <>
                  {selectedImages?.length > 0 && (
                     <Divider type="vertical" className="!h-6 !mx-1" />
                  )}
                  <Tooltip title={t("View more")}>
                     <Button
                        type="text"
                        loading={isLoadingProvider}
                        disabled={isLoadingProvider}
                        onClick={viewMore}
                        icon={<DoubleRightOutlined className="icon-inherit" />}
                        iconPosition="end"
                        aria-label={t("View more")}
                        className="view-more-btn !text-white !bg-[var(--neutral-950)] hover:!bg-[var(--neutral-900)] !rounded-lg !px-3"
                     >
                        <span className="view-more-label">{t("View more")}</span>
                     </Button>
                  </Tooltip>
               </>
            )}
            </div>
         </div>

         {/* PROVIDER LIST + LIST CONTAINER - Flex Row */}
         <div className="flex px-3 gap-3 flex-1 overflow-hidden">
            {/* PROVIDER LIST - 68px width, vertical */}
            {/* shrink-0: mã ảnh dài của hãng đang xem không được bóp mất cột hãng. */}
            <div className="w-[80px] shrink-0 overflow-y-auto pb-2 flex flex-col gap-3 h-fit">
               <div className="child !flex-col !w-[68px] gap-3">
                  {renderProvider()}
               </div>
            </div>

            {/* LIST - Full remaining width, scrollable */}

            <div className="flex-1 min-w-0">
               <Spin
                  className="flex-1 !w-full"
                  // @ts-ignore
                  spinning={searchState[`loading_provider_${providerCode}`]}
               >
                  <div
                     id="explore-result-scroll"
                     ref={scrollRef}
                     className="h-full overflow-y-auto pb-4 w-full"
                  >
                     <div className="flex flex-col gap-4">
                        {data?.length === 0 &&
                           /* @ts-ignore */
                           !searchState[`loading_provider_${providerCode}`] && (
                              <NoData fontText={"18px"} />
                           )}
                        <InfiniteScroll
                           dataLength={visibleData.length}
                           next={fetchMore}
                           scrollableTarget="explore-result-scroll"
                           hasMore={hasMore}
                           loader={null}

                        >
                           {visibleData?.length > 0 &&
                              visibleData.map((item: any, ind: number) => (
                                 <div key={item?.id ?? ind} data-result-index={ind}>
                                    <CardSearchResult
                                       item={item}
                                       handleViewInfo={handleViewInfo}
                                       providerName={providerName}
                                       selectedImages={selectedImages}
                                       setSelectedImages={setSelectedImages}
                                       providerId={providerId}
                                       handleShowFootPrint={(item: ImageItem) =>
                                          showFootprint(item, dispatch, mapInstance)
                                       }
                                       handleMouseLeaveItem={(item: ImageItem) =>
                                          mouseLeaveItem(item, engine)
                                       }
                                       handleMouseMoveItem={(item: ImageItem) =>
                                          mouseMoveItem(item, engine)
                                       }
                                       handleRemoveFootPrint={(item: ImageItem) =>
                                          removeFootprint(item, dispatch)
                                       }
                                       panTo={(item: ImageItem) =>
                                          flyToGeometry(item?.geometry, mapInstance)
                                       }
                                       handleAddCart={(ids: string[]) =>
                                          addImagesToCart(ids, dispatch, providerCode, idsImageInCart, t)
                                       }
                                       handleRemoveFromCart={(ids: string[]) =>
                                          removeImagesFromCart(ids, dispatch, providerCode, idsImageInCart, t)
                                       }
                                       handleShowImageOnMap={(item: ImageItem) =>
                                          showImageOnMap(item, dispatch, providerCode, mapInstance)
                                       }
                                       handleRemoveImage={(item: ImageItem) =>
                                          removeImageFromMap(item, dispatch)
                                       }
                                    />
                                 </div>
                              ))}
                        </InfiniteScroll>

                        {/* Fallback when the list cannot be scrolled (zoomed out). */}
                        {hasMore && visibleData.length > 0 && (
                           <div className="flex justify-center py-3">
                              <Button
                                 type="text"
                                 loading={isLoadingProvider}
                                 disabled={isLoadingProvider}
                                 onClick={viewMore}
                                 className="!text-white !bg-[var(--neutral-950)] hover:!bg-[var(--neutral-900)] !rounded-lg !px-4"
                              >
                                 {t("View more")}
                              </Button>
                           </div>
                        )}
                     </div>
                  </div>
               </Spin>
            </div>
         </div>

         {shouldRenderViewInfo && (
            <CommonComponents.ModalUI
               open={openViewInfo}
               title={t("Information")}
               toggle={() =>
               {
                  toggleViewInfo();
               }}
               handleConfirm={() => { }}
               hiddenFooter={true}
               width={1120}
            >
               <Spin spinning={loadingViewInfo}>
                  <ImageInfo data={selectedView} open={openViewInfo} />
               </Spin>
            </CommonComponents.ModalUI>
         )}
      </div>
   );
}
